import { useCallback, useEffect, useState } from 'react';
import apiService from '../services/apiService';
import { ALL_LOCATIONS, getSelectedLocation } from '../components/LocationSwitcher';
import { useAuth } from '../contexts/AuthContext';
import { isStudioScopedRole } from '../utils/roles';

export interface MyLocation {
  _id: string;
  studioName: string;
  branchName?: string | null;
  branchCode?: string | null;
  city?: string | null;
  // Shown on the Studio Profile location cards; all from `brands/my`.
  state?: string | null;
  address?: string | null;
  phoneNumber?: string | null;
  images?: string[] | null;
  isActive?: boolean;
  branchIsActive?: boolean;
  /** Present for nav gating — see anyLocationHas. */
  studioPermissions?: Record<string, any> | null;
  whitelabelConfig?: Record<string, any> | null;
}

/**
 * Does ANY location in this brand have this flag on?
 *
 * The nav reads flags off `admin.studio`, which under "All locations" is just
 * the branch the person signed in as. A brand that turned seat selection on at
 * one location lost the Rooms tab while viewing "All" and got it back on
 * switching — a tab that blinks in and out with no explanation.
 *
 * `path` is dotted, e.g. 'studioPermissions.enableSeatSelection'.
 */
export function anyLocationHas(
  locations: MyLocation[],
  path: string,
  predicate: (v: any) => boolean = (v) => v === true,
): boolean {
  return locations.some((loc) => {
    const v = path
      .split('.')
      .reduce<any>((acc, k) => (acc == null ? acc : acc[k]), loc as any);
    return predicate(v);
  });
}

/**
 * The label for a branch, in the one shape the whole panel agrees on.
 *
 * `branchName` is `null` for most studios and `city` can be absent entirely —
 * `studioName` is the only field the schema guarantees. So the chain ends
 * there rather than rendering an empty chip.
 *
 * Lifted verbatim from BrandDashboard, which was the only place that had it.
 */
export function branchLabel(loc?: MyLocation | null): string {
  if (!loc) return '';
  return loc.branchName || loc.studioName || '';
}

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  WHICH LOCATIONS DO I HOLD
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `GET /brands/my` was being fetched independently by LocationSwitcher,
 * BrandDashboard and Dashboard — three requests for the same answer on every
 * page load, each with its own copy of the `Location` interface. Anything that
 * wants to label a row by branch would have been a fourth.
 *
 * Cached for the life of the tab. The set only changes when someone's grants
 * change, which ends the session anyway, and `LocationSwitcher` reloads the
 * window on every switch — so there is no staleness window a user can observe.
 */
/** Kept for callers; the cache now lives in apiService.getMyBrand (per token). */
export function invalidateMyLocations(): void {
  /* no-op — see apiService.getMyBrand */
}

export type LocationRole = 'brand_owner' | 'studio_owner' | 'manager' | 'instructor';

/** Rank of a per-location role; higher = more authority. */
const ROLE_RANK: Record<string, number> = {
  instructor: 1,
  manager: 2,
  studio_owner: 3,
  brand_owner: 3,
};

/**
 * Does `role` meet `min`? `'owner'` = studio_owner / brand_owner, `'manager'`
 * = manager or above.
 */
export function roleMeets(role: string | null | undefined, min: 'owner' | 'manager'): boolean {
  const need = min === 'owner' ? 3 : 2;
  return (ROLE_RANK[role ?? ''] ?? 0) >= need;
}

/**
 * The ids of the locations where this account holds at least `min`, or null
 * when the server did not send `roleByStudio` (older backend) — callers then
 * do not filter.
 */
export function locationsWithRole(
  roleByStudio: Record<string, string> | null | undefined,
  min: 'owner' | 'manager',
): string[] | null {
  if (!roleByStudio || typeof roleByStudio !== 'object') return null;
  return Object.entries(roleByStudio)
    .filter(([, r]) => roleMeets(r, min))
    .map(([id]) => id);
}

async function fetchLocations(): Promise<{
  studios: MyLocation[];
  roleByStudio: Record<string, LocationRole> | null;
}> {
  const mine: any = await apiService.getMyBrand();
  const rbs = mine?.roleByStudio;
  return {
    studios: (mine?.studios ?? []) as MyLocation[],
    roleByStudio: rbs && typeof rbs === 'object' ? rbs : null,
  };
}

/**
 * Is the seat-selection feature available to this account at all?
 *
 * The same rule the navigation uses to show or hide "Rooms & Layouts"
 * (Layout.tsx). Anything that tells an operator to ATTACH A ROOM has to ask
 * this first: the instruction is useless — and confusing — to a studio that
 * has no Rooms page to do it on.
 *
 * "Anywhere in the brand", not "at the branch I signed in as", for the reason
 * anyLocationHas exists: under "All locations" `admin.studio` is only the
 * default branch, so a brand that enabled rooms at one location would see the
 * feature blink in and out as they switched.
 */
export function seatSelectionAvailable(
  admin: any,
  locations: MyLocation[],
): boolean {
  return (
    admin?.studio?.studioPermissions?.enableSeatSelection === true ||
    anyLocationHas(locations, 'studioPermissions.enableSeatSelection')
  );
}

export function useMyLocations() {
  const { admin } = useAuth();
  const isStudioRole = isStudioScopedRole(admin?.role);

  const [locations, setLocations] = useState<MyLocation[]>([]);
  const [roleByStudio, setRoleByStudio] = useState<Record<string, LocationRole> | null>(null);
  const [loading, setLoading] = useState(false);
  // True once the list has been asked for and answered (or failed) — lets a
  // page wait for it instead of rendering a single-location view first and
  // then jumping. Always true for platform admins, who have no list.
  const [loaded, setLoaded] = useState(!isStudioRole);

  useEffect(() => {
    if (!isStudioRole) return;
    let alive = true;
    setLoading(true);
    fetchLocations()
      .then((res) => {
        if (!alive) return;
        setLocations(res.studios);
        setRoleByStudio(res.roleByStudio);
      })
      .catch(() => {
        // A failure here must not blank a page — it only costs the branch
        // chips. The list itself is already on screen.
        if (alive) {
          setLocations([]);
          setRoleByStudio(null);
        }
      })
      .finally(() => {
        if (alive) {
          setLoading(false);
          setLoaded(true);
        }
      });
    return () => {
      alive = false;
    };
  }, [isStudioRole]);

  /** id → location, for labelling a row by its `studio._id`. */
  const byId = useCallback(
    (id?: string | null): MyLocation | undefined =>
      id ? locations.find((l) => l._id === String(id)) : undefined,
    [locations],
  );

  const selected = getSelectedLocation(admin?.id);

  return {
    locations,
    loading,
    loaded,
    byId,
    /** studio id → this account's role there; null if the server omitted it. */
    roleByStudio,
    /**
     * True only when the aggregate view is actually showing more than one
     * place. A single-location owner is on "All" by default and must not see
     * a branch chip on every row telling them what they already know.
     */
    isAllLocations: selected === ALL_LOCATIONS && locations.length > 1,
    selectedLocationId: selected === ALL_LOCATIONS ? null : selected,
  };
}
